/**
 * 🌟 Premium Partner Service - Fleet Partner Agreement Management
 * Handles premium partner agreements with custom commission rates
 */

import { getApiBaseUrl } from '@/config/environment';


export interface Agreement {
  id: string;
  host: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    fullName: string;
    phone?: string;
    partnerTier: 'standard' | 'fleet' | 'premium';
    vehicleCount: number;
  };
  agreementType: 'revenue_share' | 'fixed_commission' | 'hybrid';
  commission: {
    platformFeePercentage: number;
    bookingFeePercentage: number;
    platformFeeFlatAmount?: number | null;
    bookingFeeFlatAmount?: number | null;
  };
  minimumRequirements: {
    monthlyBookings?: number;
    monthlyRevenue?: number;
    vehicleCount?: number;
  };
  specialTerms: {
    fuelResponsibility: 'partner' | 'renter' | 'split';
    driverProvision: 'partner' | 'renter' | 'optional';
    insuranceResponsibility: 'partner' | 'polarride' | 'shared';
    customSecurityDepositPercentage?: number | null;
  };
  contract: {
    startDate: string;
    endDate?: string | null;
    autoRenewal: boolean;
    renewalNoticeDays?: number;
    renewalTerms?: string | null;
  };
  status: 'pending' | 'active' | 'suspended' | 'terminated' | 'expired';
  accountManager?: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  metadata: {
    createdBy: { firstName?: string; lastName?: string; email?: string };
    approvedBy?: { firstName?: string; lastName?: string; email?: string } | null;
    approvedAt?: string | null;
    createdAt: string;
    updatedAt: string;
    terminatedBy?: { firstName?: string; lastName?: string; email?: string } | null;
    terminatedAt?: string | null;
    terminationReason?: string | null;
  };
  internalNotes?: string;
  partnerTerms?: string;
  documentUrls?: string[];
}

export interface CreateAgreementData {
  hostId: string;
  agreementType?: 'revenue_share' | 'fixed_commission' | 'hybrid';
  platformFeePercentage: number;
  bookingFeePercentage: number;
  platformFeeFlatAmount?: number;
  bookingFeeFlatAmount?: number;
  minimumMonthlyBookings?: number;
  minimumMonthlyRevenue?: number;
  minimumVehicleCount?: number;
  fuelResponsibility?: 'partner' | 'renter' | 'split';
  driverProvision?: 'partner' | 'renter' | 'optional';
  insuranceResponsibility?: 'partner' | 'polarride' | 'shared';
  customSecurityDepositPercentage?: number;
  startDate: string;
  endDate?: string;
  autoRenewal?: boolean;
  renewalNoticeDays?: number;
  renewalTerms?: string;
  internalNotes?: string;
  partnerTerms?: string;
  documentUrls?: string[];
  createdBy: string;
}

export interface AgreementFilters {
  status?: 'pending' | 'active' | 'suspended' | 'terminated' | 'expired' | 'all';
  hostId?: string;
  tier?: 'premium' | 'fleet' | 'standard';
}

export const premiumPartnerService = {
  /**
   * Get all premium partner agreements with optional filters
   */
  async getAgreements(filters?: AgreementFilters): Promise<{
    success: boolean;
    data?: {
      agreements: Agreement[];
      total: number;
    };
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();

      if (filters?.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters?.hostId) {
        params.append('hostId', filters.hostId);
      }
      if (filters?.tier) {
        params.append('tier', filters.tier);
      }

      const queryString = params.toString();
      const url = `${getApiBaseUrl()}/api/v1/admin/premium-partners/agreements${queryString ? `?${queryString}` : ''}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching agreements:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch agreements'
      };
    }
  },

  /**
   * Get single agreement details
   */
  async getAgreement(agreementId: string): Promise<{
    success: boolean;
    data?: Agreement;
    error?: string;
  }> {
    try {

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/admin/premium-partners/agreements/${agreementId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching agreement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch agreement'
      };
    }
  },

  /**
   * Create new premium partner agreement
   */
  async createAgreement(data: CreateAgreementData): Promise<{
    success: boolean;
    message?: string;
    data?: {
      agreementId: string;
      host: any;
      agreement: any;
    };
    error?: string;
  }> {
    try {

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/admin/premium-partners/agreements`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error creating agreement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create agreement'
      };
    }
  },

  /**
   * Update existing agreement
   */
  async updateAgreement(agreementId: string, data: Partial<CreateAgreementData>): Promise<{
    success: boolean;
    message?: string;
    data?: Agreement;
    error?: string;
  }> {
    try {

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/admin/premium-partners/agreements/${agreementId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error updating agreement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update agreement'
      };
    }
  },

  /**
   * Approve agreement and activate premium partner status
   */
  async approveAgreement(
    agreementId: string,
    approvedBy: string,
    accountManagerId?: string,
    notes?: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: Agreement;
    error?: string;
  }> {
    try {

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/admin/premium-partners/agreements/${agreementId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ approvedBy, accountManagerId, notes })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error approving agreement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to approve agreement'
      };
    }
  },

  /**
   * Terminate agreement
   */
  async terminateAgreement(
    agreementId: string,
    terminatedBy: string,
    reason: string,
    hardDelete = false
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/admin/premium-partners/agreements/${agreementId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ terminatedBy, terminationReason: reason, hardDelete })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error terminating agreement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to terminate agreement'
      };
    }
  }
};
